import {
    OverlapConflict,
    OverlapFix,
    ScheduleDraft,
    ScheduleEntry,
    ScheduleLane,
} from './types'
import {
    formatDateLine,
    formatInclusiveEndInstant,
    parseInstant,
} from './datetime'

const OPEN_ENDED = Number.POSITIVE_INFINITY

export function windowEndMs(endsAt: string | null): number {
    if (!endsAt) return OPEN_ENDED
    return parseInstant(endsAt)?.getTime() ?? OPEN_ENDED
}

export function windowsOverlap(
    aStartsAt: string,
    aEndsAt: string | null,
    bStartsAt: string,
    bEndsAt: string | null,
): boolean {
    const aStart = parseInstant(aStartsAt)?.getTime()
    const bStart = parseInstant(bStartsAt)?.getTime()
    if (aStart == null || bStart == null) return false
    return aStart < windowEndMs(bEndsAt) && bStart < windowEndMs(aEndsAt)
}

export function isValidWindow(
    startsAt: string,
    endsAt: string | null,
): boolean {
    const start = parseInstant(startsAt)
    const end = endsAt ? parseInstant(endsAt) : null
    return !!start && !!end && start.getTime() < end.getTime()
}

export function sortByStart(entries: ScheduleEntry[]): ScheduleEntry[] {
    return [...entries].sort((a, b) => {
        const startDelta =
            (parseInstant(a.startsAt)?.getTime() ?? 0) -
            (parseInstant(b.startsAt)?.getTime() ?? 0)
        if (startDelta !== 0) return startDelta
        return a.id.localeCompare(b.id)
    })
}

export function isPastEntry(entry: ScheduleEntry, nowMs: number): boolean {
    return windowEndMs(entry.endsAt) <= nowMs
}

export function scheduleLanes(
    entries: ScheduleEntry[],
    options: { includeTail?: boolean } = {},
): ScheduleLane[] {
    const sorted = sortByStart(entries)
    const lanes: ScheduleLane[] = []
    sorted.forEach((entry, index) => {
        if (index > 0) {
            const previous = sorted[index - 1]
            const previousEnd = parseInstant(previous.endsAt)?.getTime()
            const nextStart = parseInstant(entry.startsAt)?.getTime()
            if (
                previousEnd != null &&
                nextStart != null &&
                previousEnd < nextStart
            ) {
                lanes.push({
                    kind: 'gap',
                    startsAt: previous.endsAt,
                    endsAt: entry.startsAt,
                })
            }
        }
        lanes.push({ kind: 'entry', entry })
    })
    if (options.includeTail && sorted.length > 0) {
        lanes.push({
            kind: 'tail',
            startsAt: sorted[sorted.length - 1].endsAt,
        })
    }
    return lanes
}

export function findOverlaps(
    draft: Pick<ScheduleDraft, 'id' | 'startsAt' | 'endsAt'>,
    entries: ScheduleEntry[],
): OverlapConflict[] {
    if (!isValidWindow(draft.startsAt, draft.endsAt)) return []
    return entries.flatMap((entry) => {
        if (entry.id === draft.id) return []
        if (
            !windowsOverlap(
                draft.startsAt,
                draft.endsAt,
                entry.startsAt,
                entry.endsAt,
            )
        ) {
            return []
        }
        return [
            {
                otherId: entry.id,
                otherName: entry.name,
                otherStartsAt: entry.startsAt,
                otherEndsAt: entry.endsAt,
            },
        ]
    })
}

export function overlapMessage(
    conflict: OverlapConflict,
    timeZone?: string,
): string {
    return `This overlaps ${conflict.otherName}, which runs until ${formatInclusiveEndInstant(conflict.otherEndsAt, timeZone)}. Only one promotion can run at a time.`
}

type ClosedWindow = { startsAt: string; endsAt: string }

export function overlapFixes(
    draft: ClosedWindow,
    conflict: OverlapConflict,
    timeZone?: string,
): {
    thisFix: Extract<OverlapFix, { target: 'this' }>
    otherFix: Extract<OverlapFix, { target: 'other' }>
} {
    return {
        thisFix: thisOverlapFix(draft, conflict, timeZone),
        otherFix: otherOverlapFix(draft, conflict, timeZone),
    }
}

function thisOverlapFix(
    draft: ClosedWindow,
    conflict: OverlapConflict,
    timeZone?: string,
): Extract<OverlapFix, { target: 'this' }> {
    if (isValidWindow(conflict.otherEndsAt, draft.endsAt)) {
        return {
            target: 'this',
            kind: 'startThisLater',
            title: 'This promotion',
            description: `Keep ${conflict.otherName} as-is. Start this after it ends.`,
            label: `Start this ${formatDateLine(conflict.otherEndsAt, timeZone)}`,
            nextStartsAt: conflict.otherEndsAt,
        }
    }
    if (isValidWindow(draft.startsAt, conflict.otherStartsAt)) {
        return {
            target: 'this',
            kind: 'endThisSooner',
            title: 'This promotion',
            description: `Keep ${conflict.otherName} as-is. End this when it starts.`,
            label: `End this ${formatInclusiveEndInstant(conflict.otherStartsAt, timeZone)}`,
            nextEndsAt: conflict.otherStartsAt,
        }
    }
    return {
        target: 'this',
        kind: 'startThisLater',
        title: 'This promotion',
        description: `Keep ${conflict.otherName} as-is. Move this to start after it ends.`,
        label: `Start this ${formatDateLine(conflict.otherEndsAt, timeZone)}`,
        nextStartsAt: conflict.otherEndsAt,
        nextEndsAt: shiftEndByStartDelta(
            draft.startsAt,
            draft.endsAt,
            conflict.otherEndsAt,
        ),
    }
}

function otherOverlapFix(
    draft: ClosedWindow,
    conflict: OverlapConflict,
    timeZone?: string,
): Extract<OverlapFix, { target: 'other' }> {
    if (isValidWindow(conflict.otherStartsAt, draft.startsAt)) {
        return {
            target: 'other',
            kind: 'endOtherSooner',
            title: conflict.otherName,
            description:
                "Keep this promotion's dates. End the overlapping one when this starts.",
            label: `End ${conflict.otherName} ${formatInclusiveEndInstant(draft.startsAt, timeZone)}`,
            otherId: conflict.otherId,
            otherName: conflict.otherName,
            nextOtherEndsAt: draft.startsAt,
        }
    }
    if (isValidWindow(draft.endsAt, conflict.otherEndsAt)) {
        return {
            target: 'other',
            kind: 'startOtherLater',
            title: conflict.otherName,
            description:
                "Keep this promotion's dates. Start the overlapping one after this ends.",
            label: `Start ${conflict.otherName} ${formatDateLine(draft.endsAt, timeZone)}`,
            otherId: conflict.otherId,
            otherName: conflict.otherName,
            nextOtherStartsAt: draft.endsAt,
        }
    }
    const shiftedEnd = shiftEndByStartDelta(
        conflict.otherStartsAt,
        conflict.otherEndsAt,
        draft.endsAt,
    )
    return {
        target: 'other',
        kind: 'startOtherLater',
        title: conflict.otherName,
        description:
            "Keep this promotion's dates. Move the overlapping one to start after this ends.",
        label: `Start ${conflict.otherName} ${formatDateLine(draft.endsAt, timeZone)}`,
        otherId: conflict.otherId,
        otherName: conflict.otherName,
        nextOtherStartsAt: draft.endsAt,
        nextOtherEndsAt: shiftedEnd,
    }
}

function shiftEndByStartDelta(
    previousStart: string,
    previousEnd: string,
    nextStart: string,
): string | undefined {
    const start = parseInstant(previousStart)?.getTime()
    const end = parseInstant(previousEnd)?.getTime()
    const next = parseInstant(nextStart)?.getTime()
    if (start == null || end == null || next == null) return undefined
    return new Date(next + (end - start)).toISOString()
}

export function applyStartThisLater(
    entries: ScheduleEntry[],
    draftId: string,
    nextStartsAt: string,
): ScheduleEntry[] {
    return entries.map((entry) =>
        entry.id === draftId ? { ...entry, startsAt: nextStartsAt } : entry,
    )
}

export function applyOtherPatch(
    entries: ScheduleEntry[],
    otherId: string,
    patch: Partial<Pick<ScheduleEntry, 'startsAt' | 'endsAt'>>,
): ScheduleEntry[] {
    return entries.map((entry) => {
        if (entry.id !== otherId) return entry
        return {
            ...entry,
            ...(patch.startsAt ? { startsAt: patch.startsAt } : {}),
            ...(patch.endsAt ? { endsAt: patch.endsAt } : {}),
        }
    })
}

export function applyEndOtherSooner(
    entries: ScheduleEntry[],
    otherId: string,
    nextOtherEndsAt: string,
): ScheduleEntry[] {
    return applyOtherPatch(entries, otherId, { endsAt: nextOtherEndsAt })
}
